import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useTourHandler } from '@/contexts/tour-handler'
import { useFieldWithHelp } from '@/hooks/useFieldWithHelp'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import BackIcons from '@/icons/BackIcons'
import CheckIcons from '@/icons/CheckIcons'
import useAuthStore from '@/stores/useAuthStore'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { GridHookForm, FormItem } from 'vnetwork-ui'
import { createContactSource } from '../../services'
import { ContactSourceCreatePayload } from '../../types/payload'
import {
    ContactChanel,
    ContactStatusTypes,
    IndustryOptions,
} from '../../types/utils'
import EmailField from './EmailField'
import { createContactsSchema } from './schema'
import { useContactSourceTour } from './use-contact-source-tour'
import { CONTACT_ASSIGNEE_DEPARTMENT_IDS } from '@/constants/departments'

const FORM_STORAGE_KEY = 'contact-source-create-form'

const Create: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const { t: tMarketing } = useTranslation('marketing')

    // Help button for lead status classification
    const { FieldWithHelp: StatusFieldWithHelp } = useFieldWithHelp({
        modalName: 'ContactMarketingModals/LeadStatusLegendModal',
        title: t('marketing:lead_flow.title'),
    })

    // Tour for Contact Source Create form
    const { startTour } = useContactSourceTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const navigate = useNavigate()
    const { employee } = useAuthStore()

    const defaultValues = useMemo<ContactSourceCreatePayload>(
        () => ({
            contact_source_name: '',
            contact_source_position: '',
            contact_source_email: '',
            contact_source_phone: '',
            contact_source_channel: 'form',
            contact_source_company: '',
            contact_source_profile: '',
            contact_source_website: '',
            contact_source_notes: '',
            contact_source_status: 'UNCLASSIFIED',
            contact_source_industry: '',
            contact_source_interested_services: [],
            contact_source_object_id: employee?.employee_id || -1,
            contact_source_object_type: 'Employee',
            contact_source_referrer_id: -1,
            contact_source_referrer_type: '',
            contact_source_assignee_ids: null,
            website_referrer: '',
            company_size: '',
            deployment_time: '',
            specific_needs: '',
            customer_lead_type: '',
        }),
        [employee?.employee_id]
    )

    const formControl = useForm<ContactSourceCreatePayload>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(createContactsSchema(t)),
    })

    // Persist form data to localStorage
    const { clearOnly } = useFormPersistence(
        formControl,
        FORM_STORAGE_KEY,
        defaultValues
    )

    const { mutateAsync: createContactSourceMutation, isPending } =
        useMutationWithHandler(
            {
                mutationFn: createContactSource,
            },
            {
                reactQueryKeys: QUERY_KEYS.MARKETING,
                successMessage: t('create-success'),
                onSuccess: () => {
                    clearOnly()
                    navigate('/contacts')
                },
            }
        )

    const onSubmit = async (data: ContactSourceCreatePayload) => {
        try {
            await createContactSourceMutation({
                ...data,
                // BE expects contact_source_object_id as a string (pgtype.Text —
                // polymorphic object id). Coerce here so a numeric employee_id
                // (from defaults or persisted form state) doesn't fail
                // ShouldBindJSON with 400 invalid-request-parameters.
                contact_source_object_id: `${data.contact_source_object_id}`,
            })
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.customerMkt,
                        title: t('customer-mkt'),
                    },
                ]}
            />
            <Paper>
                <GridHookForm
                    formControl={formControl}
                    onSubmit={onSubmit}
                    head={{
                        title: t('customer-mkt-create'),
                    }}
                    data={[
                        {
                            formItemProps: {
                                name: 'contact_source_name',
                                type: 'textfield',
                                label: t('contact-source-name'),
                                placeholder: t('contact-source-name'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_position',
                                type: 'textfield',
                                label: t('position'),
                                placeholder: t('position'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_phone',
                                type: 'textfield',
                                label: t('contact-source-phone'),
                                placeholder: t('contact-source-phone'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            renderEditComponent: (control) => (
                                <div id="contact_source_email">
                                    <EmailField control={control} />
                                </div>
                            ),
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_company',
                                type: 'textfield',
                                label: t('company'),
                                placeholder: t('company'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_website',
                                type: 'textfield',
                                label: 'Website',
                                placeholder: 'Website',
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_profile',
                                type: 'textfield',
                                label: t('contact-source-profile'),
                                placeholder: t('contact-source-profile'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_industry',
                                type: 'select',
                                label: t('contact-source-industry'),
                                placeholder: t('contact-source-industry'),
                                options: IndustryOptions,
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_channel',
                                type: 'select',
                                label: t('contact-source-channel'),
                                placeholder: t('contact-source-channel'),
                                options: ContactChanel(tMarketing),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            className: 'md:col-span-2',
                            renderEditComponent: (control) => (
                                <ProductGroupSelect
                                    control={control}
                                    name="contact_source_interested_services"
                                    multiple
                                    showLabel
                                    label={t(
                                        'contact-source-interested-services'
                                    )}
                                    placeholder={t(
                                        'contact-source-interested-services'
                                    )}
                                />
                            ),
                        },
                        {
                            formItemProps: {
                                name: 'company_size',
                                type: 'textfield',
                                label: t('company-size'),
                                placeholder: t('company-size'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'deployment_time',
                                type: 'textfield',
                                label: t('deployment-time'),
                                placeholder: t('deployment-time'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'website_referrer',
                                type: 'textfield',
                                label: t('website-referrer'),
                                placeholder: t('website-referrer'),
                            },
                            className: 'md:col-span-2',
                        },
                        {
                            className: 'md:col-span-4',
                            renderEditComponent: (control) => (
                                <StatusFieldWithHelp>
                                    <FormItem
                                        control={control}
                                        name="contact_source_status"
                                        type="select"
                                        label={t('contact-source-status')}
                                        placeholder={t('contact-source-status')}
                                        options={ContactStatusTypes(t)?.slice(
                                            1
                                        )}
                                    />
                                </StatusFieldWithHelp>
                            ),
                        },
                        {
                            formItemProps: {
                                name: 'specific_needs',
                                type: 'textarea',
                                label: t('specific-needs'),
                                placeholder: t('specific-needs'),
                            },
                            className: 'md:col-span-4',
                        },
                        {
                            formItemProps: {
                                name: 'contact_source_notes',
                                type: 'textarea',
                                label: t('note'),
                                placeholder: t('note'),
                            },
                            className: 'md:col-span-4',
                        },
                        {
                            className: 'md:col-span-4',
                            renderEditComponent: (control) => (
                                <EmployeeSelect
                                    control={control}
                                    name="contact_source_assignee_ids"
                                    multiple
                                    onlyActive
                                    label="marketing:contact-source-assignee"
                                    departmentIds={
                                        CONTACT_ASSIGNEE_DEPARTMENT_IDS
                                    }
                                />
                            ),
                        },
                    ]}
                    actions={[
                        {
                            startIcon: <BackIcons />,
                            title: t('cancle-operation'),
                            type: 'button',
                            variant: 'outlined',
                            handler: () => navigate(paths.customerMkt),
                            disabled: isPending,
                        },
                        {
                            startIcon: <CheckIcons />,
                            title: t('customer-mkt-create'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isPending,
                            disabled: isPending,
                        },
                    ]}
                />
            </Paper>
        </div>
    )
}

export default Create
